import { useEffect, useRef, useState } from 'react';
import { useLocale } from '../i18n/Locale.js';
import { typingInto } from '../ui/keys.js';
import '../auth-pages.css';

/**
 * Ctrl+Q, like the Tibia client: "do you really want to log out?". Confirm
 * leaves the character and returns to the character list (the account stays
 * signed in, owner 16/10); the hunt keeps
 * running on the server like any other exit. Not while typing (chat, search).
 */
export function LogoutShortcut({ onLogout }: { onLogout: () => void }) {
  const { locale } = useLocale();
  const en = locale === 'en';
  const [open, setOpen] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (open) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }
        return;
      }
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      if (event.code !== 'KeyQ' && event.key.toLowerCase() !== 'q') return;
      if (typingInto(event)) return;
      event.preventDefault();
      setOpen(true);
    };
    // Capture: the dialog's Escape must win over windows that also close on Escape.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  if (!open) return null;
  return (
    <>
      <div className="hzj-backdrop" aria-hidden onClick={() => setOpen(false)} />
      <section
        className="hzw hzj hzj--small hz-logout"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="hz-logout-title"
        data-testid="logout-confirm"
      >
        <div className="hzj-body">
          <span className="hzj-kicker">Logout</span>
          <h2 className="hzj-title" id="hz-logout-title">{en ? 'Log out?' : 'Sair do jogo?'}</h2>
          <p className="hzj-lede">
            {en
              ? 'Are you sure you want to log out? Everything is saved on the server (a running hunt goes on) and you go back to the character list.'
              : 'Tem certeza de que quer sair? Tudo fica salvo no servidor (uma caçada em andamento continua) e você volta para a seleção de personagens.'}
          </p>
        </div>
        <div className="hzj-actions hzj-actions--split">
          <button type="button" className="hzj-btn" onClick={() => setOpen(false)}>{en ? 'Cancel' : 'Cancelar'}</button>
          <button
            ref={confirmRef}
            type="button"
            className="hzj-btn hzj-btn--main"
            data-testid="logout-yes"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            {en ? 'Log out' : 'Sair'}
          </button>
        </div>
      </section>
    </>
  );
}
